<template>
  <DetailCard :loading="loading" :error="errorMessage">
    <template #heading>
      <span class="pi pi-home"></span>
      {{ $t(isLandParcel ? `messages.detail.outBuildings.titleLand` : `messages.detail.outBuildings.title`) }}
    </template>
    <div>
      <DataTable
        striped-rows
        row-hover
        :value="buildings"
        :filter="ehrId"
        :filter-function="filterTable"
        selection-mode="multiple"
        v-model:selection="selectedBuildings"
        @row-select="onRowSelect"
        @row-select-all="onRowSelectAll"
        @row-unselect="onRowUnselect"
      >
        <Column selection-mode="multiple" header-style="width: 3rem"></Column>
        <Column v-for="field in fields" :field="field.key" :header="field.label">
          <template #body="slotProps">
            <span>{{ slotProps.data[field.key] }}</span>
          </template>
        </Column>
        <template #empty>
          <div class="flex justify-center items-center h-full">
            <p class="text-gray-500">{{ $t('messages.no-results') }}</p>
          </div>
        </template>
      </DataTable>

      <div class="p-4 bg-gray-50 dark:bg-gray-800 grid grid-cols-2 gap-4">
        <div v-for="building in selectedBuildings" :key="building.ehrCode" class="max-w-4xl">
          <h4 class="text-xl font-bold">{{ building.name }}</h4>

          <ProgressSpinner v-if="!building.ehrData" class="w-10 h-10 m-4" />

          <div v-else class="flex flex-col">
            <h5 class="text-lg font-bold my-2">{{ $t(`messages.detail.ehrGeneral.title`) }}</h5>
            <div v-for="field in generalFields" :key="field" class="grid grid-cols-2 mt-1">
              <label :for="field + building.ehrCode" class="text-sm">
                {{ $t(`messages.detail.ehrGeneral.${field}`) }}
              </label>
              <InputText
                :id="field + building.ehrCode"
                v-model="building.ehrData.general[field]"
                size="small"
                @update:model-value="saveSelectedBuildings"
              />
            </div>

            <div v-if="building.ehrData.general['ehitise-liik'] != 'Rajatis'">
              <h5 class="text-lg font-bold my-2">
                {{ $t(`messages.detail.ehrTechnical.title`) }}
              </h5>
              <div v-for="field in technicalFields" :key="field" class="grid grid-cols-2 mt-1">
                <label :for="field + building.ehrCode" class="text-sm">
                  {{ $t(`messages.detail.ehrTechnical.${field}`) }}
                </label>
                <InputText
                  :id="field + building.ehrCode"
                  v-model="building.ehrData.technical[field]"
                  size="small"
                  @update:model-value="saveSelectedBuildings"
                />
              </div>

              <h5 class="text-lg font-bold my-2">
                {{ $t(`messages.detail.ehrConstruction.title`) }}
              </h5>
              <div
                v-for="field in constructionFields"
                :key="field"
                class="grid grid-cols-2 mt-1"
              >
                <label :for="field + building.ehrCode" class="text-sm">
                  {{ $t(`messages.detail.ehrConstruction.${field}`) }}
                </label>
                <InputText
                  :id="field + building.ehrCode"
                  v-model="building.ehrData.construction[field]"
                  size="small"
                  @update:model-value="saveSelectedBuildings"
                />
              </div>

              <h5 class="text-lg font-bold my-2">
                {{ $t(`messages.detail.ehrCommunal.title`) }}
              </h5>
              <div v-for="field in communalFields" :key="field" class="grid grid-cols-2 mt-1">
                <label :for="field + building.ehrCode" class="text-sm">
                  {{ $t(`messages.detail.ehrCommunal.${field}`) }}
                </label>
                <InputText
                  :id="field + building.ehrCode"
                  v-model="building.ehrData.communal[field]"
                  size="small"
                  @update:model-value="saveSelectedBuildings"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { ProgressSpinner } from 'primevue'
import { shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DetailCard from '@/components/detail/DetailCard.vue'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'

const store = useDetailStore()
const evaluationStore = useEvaluationStore()
const { buildings, selectedBuildings, ehrId, cadasterId } = storeToRefs(store)

const { t } = useI18n()

const loading = shallowRef(false)
const errorMessage = shallowRef('')

const fields = [
  {
    key: 'type',
    label: t(`messages.detail.ehrGeneral.ehitise-liik`),
  },
  {
    key: 'name',
    label: t(`messages.detail.ehrGeneral.ehitise-nimetus`),
  },
  {
    key: 'usage',
    label: t(`messages.detail.ehrGeneral.peamine-kasutusotstarve`),
  },
  {
    key: 'status',
    label: t(`messages.detail.ehrGeneral.ehitise-seisund`),
  },
  {
    key: 'ehrCode',
    label: t(`messages.detail.ehrGeneral.ehrkood`),
  },
  {
    key: 'surface',
    label: t(`messages.detail.ehrTechnical.snp`) + ' (m2)',
  },
]

const generalFields = [
  'ehitise-liik',
  'ehitise-nimetus',
  'ehrkood',
  'kasutuselevotu-aasta',
  'ehitise-seisund',
  'peamine-kasutusotstarve',
]

const technicalFields = [
  'ehitisealune-pind',
  'maapealseid-korruseid',
  'maa-alused-korrused',
  'snp',
  'koetav-pind',
]

const constructionFields = [
  'vundamendi_liik',
  'konstruktsioonide_materjal',
  'valisseina_valisviimistlus',
  'valisseina_liik',
  'katuste_katuselagede_materjal',
  'vahelagede_materjal',
  'katusekate',
  'hoone_seisukord',
]

const communalFields = [
  'elektrisysteemi_liik',
  'veevarustuse_liik',
  'kanalisatsiooni_liik',
  'soojusvarustuse_liik',
]

const filterTable = (row, filter) => {
  return row.ehrCode != filter
}

const saveSelectedBuildings = () => {
  evaluationStore.save({
    assessment_data: {
      selected_buildings: selectedBuildings.value
        .filter(b => b.ehrData)
        .map(b => ({
          ehrCode: b.ehrCode,
          ehrData: b.ehrData,
        })),
    },
  })
}

const restoreSelectedBuildings = async (savedBuildings) => {
  if (!savedBuildings?.length || !buildings.value?.length) return

  const buildingsToSelect = []
  for (const saved of savedBuildings) {
    const building = buildings.value.find(b => b.ehrCode === saved.ehrCode)
    if (building) {
      // Restore saved EHR data (user edits overlaid on re-fetched data)
      if (saved.ehrData) {
        building.ehrData = saved.ehrData
      }
      buildingsToSelect.push(building)
    }
  }
  selectedBuildings.value = buildingsToSelect
}

const loadBuildingsData = async cadasterId => {
  if (!cadasterId) return

  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/search?query=' + cadasterId)
    // Keep only EHR ehitis rows — the search can also return the parcel
    // itself (resultType 'parcel', no ehrCode) when the kataster has no
    // buildings (RED-265).
    buildings.value = (response.data.result.data || []).filter(row => row.ehrCode)
    selectedBuildings.value = []
  } catch (err) {
    console.error('Failed to load buildings data:', err)
    errorMessage.value = t('messages.no-results')
    buildings.value = []
  } finally {
    loading.value = false
  }
}

const onRowSelectAll = event => {
  event.data.forEach(building => {
    loadEhrData(building).then(saveSelectedBuildings)
  })
}

const onRowSelect = event => {
  const building = event.data
  loadEhrData(building).then(saveSelectedBuildings)
}

const onRowUnselect = event => {
  const building = event.data
  delete building.ehrData
  saveSelectedBuildings()
}

const loadEhrData = async building => {
  if (!building.ehrCode) return

  await axios
    .get('/xroad/ehr/' + building.ehrCode)
    .then(response => {
      building.ehrData = response.data
    })
    .catch(err => {
      console.log('loadEhrData error: ' + err)
    })
}

const props = defineProps({
  savedSelectedBuildings: { type: Array, default: () => [] },
  // Maatükk mode: no main building exists, so the list shows every ehitis
  // on the parcel (ehrId is null → filterTable excludes nothing) under the
  // "Hooned ja rajatised" title.
  isLandParcel: { type: Boolean, default: false },
})

watch(
  cadasterId,
  async newVal => {
    if (newVal) {
      await loadBuildingsData(newVal)
      if (props.savedSelectedBuildings?.length) {
        restoreSelectedBuildings(props.savedSelectedBuildings)
      }
    }
  },
  { immediate: true },
)
</script>

<style scoped>
  .form-row {
    margin-bottom: 0;
  }
</style>
